import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL || ''
const SCHEDULE_URL = `${API_URL}/api/schedule`

// Get auth token from localStorage
const getAuthToken = () => {
  const auth = localStorage.getItem('kt-auth-react-v')
  if (auth) {
    const parsed = JSON.parse(auth)
    return parsed.api_token
  }
  return null
}

// Get admin token if present (for admin mode)
const getAdminToken = () => {
  return localStorage.getItem('admin_auth_token')
}

// Create axios instance with auth header
const getAxios = () => {
  const token = getAuthToken()
  const adminToken = getAdminToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (adminToken) headers['X-Admin-Token'] = adminToken
  return axios.create({ headers })
}

export interface ScheduleSlotUser {
  _id: string
  name: string
  facebook_id: string
  profile_picture_url?: string
}

export interface ScheduleSlotGroup {
  _id: string
  name: string
  group_id: string
  image_url?: string
}

export interface ScheduleSlotPost {
  _id: string
  name: string
  post_content?: string
  media_url?: string
}

export interface ScheduleSlotCampaign {
  _id: string
  name: string
}

export interface ScheduleSlotSubmittedPost {
  _id: string
  facebook_post_id?: string
  verification_status?: 'pending' | 'verified' | 'deleted' | 'error'
  verification_date?: string
  verification_error?: string
  status?: 'pending' | 'published'
}

export interface ScheduleSlot {
  _id: string
  slot_type?: 'post' | 'join_group'
  posted: boolean
  is_error: boolean
  // Present on error slots; a value starting with "warmup_skipped" means the slot
  // was intentionally dropped by the warm-up ramp (not a real failure).
  error_message?: string
  // True when this slot hit FB hard-block error 1390008 (account temporarily blocked).
  fb_hard_blocked?: boolean
  has_missing_post_id?: boolean
  time_slot: string
  post_type?: string
  user: ScheduleSlotUser | null
  group: ScheduleSlotGroup | null
  post?: ScheduleSlotPost | null
  campaign?: ScheduleSlotCampaign | null
  submitted_post?: ScheduleSlotSubmittedPost | null
  created_at: string
}

export interface ScheduleStats {
  total: number
  posted: number
  pending: number
  errors: number
  warmup?: number
  join_groups?: number
}

export interface ProfileStats {
  _id: string
  name: string
  facebook_id?: string
  profile_picture_url?: string
  slots_total: number
  slots_posted: number
  slots_pending: number
  slots_errors: number
  slots_warmup?: number
  join_groups: number
  max_daily: number
  remaining_capacity: number
  usage_percent: number
  groups_following: number
  // Group-join pacing (admin join-cap popup)
  max_join_groups_per_day?: number
  join_cap_override?: number
  effective_join_cap?: number
  is_online?: boolean
  is_parent?: boolean
  parent_name?: string | null
  last_get_groups?: string
  last_get_notifications?: string
  last_get_marketplace?: string
  xpost_cache_size?: number
  xpost_cache_checked_at?: string | null
  xpost_eligible_slots?: number
  xpost_sched_groups?: number
  xpost_eligible_sched_groups?: number
  // Groups the bot parked as unusable for THIS profile (cannot_post /
  // group_unavailable). Scheduling skips them.
  blacklisted_count?: number
}

export interface ScheduleResponse {
  success: boolean
  date: string
  stats: ScheduleStats
  profile_stats?: ProfileStats[]
  hourly_slots: Record<number, ScheduleSlot[]>
  requires_profile?: boolean // heavy account: pick a profile to load the grid
}

// Get today's scheduled posts
// profileId scopes the heavy hourly_slots to one profile (the grid only shows the
// selected profile anyway). Omit it for the profile-picker view — the server then
// returns profile_stats only (no slot docs), which keeps heavy accounts from crashing.
export const getTodaySchedule = async (profileId?: string): Promise<ScheduleResponse> => {
  const api = getAxios()
  const response = await api.get<ScheduleResponse>(`${SCHEDULE_URL}/today`, profileId ? {params: {profile: profileId}} : undefined)
  return response.data
}

// Get scheduled posts for a specific date
export const getScheduleByDate = async (date: string, profileId?: string): Promise<ScheduleResponse> => {
  const api = getAxios()
  const response = await api.get<ScheduleResponse>(`${SCHEDULE_URL}/date/${date}`, profileId ? {params: {profile: profileId}} : undefined)
  return response.data
}

// Log entry interface
export interface SlotLogEntry {
  _id: string
  message: string
  is_error: boolean
  action: string
  image_url?: string
  user?: {
    _id: string
    name: string
    facebook_id: string
  } | null
  created_at: string
}

// Get details for a specific slot
export interface SlotDetailResponse {
  success: boolean
  slot: ScheduleSlot & {
    date: string
    error_message?: string
    logs?: SlotLogEntry[]
    post: {
      _id: string
      name: string
      post_content?: string
      media_url?: string
    } | null
  }
}

export const getSlotDetails = async (slotId: string): Promise<SlotDetailResponse> => {
  const api = getAxios()
  const response = await api.get<SlotDetailResponse>(`${SCHEDULE_URL}/slot/${slotId}`)
  return response.data
}

// Trigger immediate post for a slot
export interface PostNowResponse {
  success: boolean
  message?: string
  error?: string
  post_url?: string
  facebook_post_id?: string
}

export const postNow = async (slotId: string): Promise<PostNowResponse> => {
  const api = getAxios()
  const response = await api.post<PostNowResponse>(`${SCHEDULE_URL}/post-now/${slotId}`)
  return response.data
}

// Wipe a whole day for the account: delete its post slots + submitted posts
export interface ClearDayResponse {
  success: boolean
  date?: string
  deleted_slots?: number
  deleted_submitted_posts?: number
  error?: string
}

export const clearScheduleDay = async (date: string): Promise<ClearDayResponse> => {
  const api = getAxios()
  const response = await api.post<ClearDayResponse>(`${SCHEDULE_URL}/clear-day`, { date })
  return response.data
}

// Reset error status for a slot
export interface ResetErrorResponse {
  success: boolean
  message?: string
  error?: string
  resetCount?: number
}

export const resetError = async (slotId: string): Promise<ResetErrorResponse> => {
  const api = getAxios()
  const response = await api.post<ResetErrorResponse>(`${SCHEDULE_URL}/reset-error/${slotId}`)
  return response.data
}

// Reset all today's error slots
export const resetAllTodayErrors = async (): Promise<ResetErrorResponse> => {
  const api = getAxios()
  const response = await api.post<ResetErrorResponse>(`${SCHEDULE_URL}/reset-all-errors/today`)
  return response.data
}

// Admin-only: reset today's error slots across every account in the system
export interface ResetAllSystemWideResponse extends ResetErrorResponse {
  breakdown?: { post_errors: number; post_phantoms: number; join_group_errors: number }
}
export const resetAllTodayErrorsSystemWide = async (): Promise<ResetAllSystemWideResponse> => {
  const adminToken = getAdminToken()
  if (!adminToken) {
    return { success: false, error: 'Admin token missing' }
  }
  const response = await axios.post<ResetAllSystemWideResponse>(
    `${API_URL}/admin/schedule/reset-all-errors-today`,
    {},
    { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true }
  )
  return response.data
}

// Reset last_get_following for profiles with < 10 groups
export const resetGetGroups = async (): Promise<ResetErrorResponse> => {
  const api = getAxios()
  const response = await api.post<ResetErrorResponse>(`${SCHEDULE_URL}/reset-get-groups`)
  return response.data
}


// Reset join group slot for retry
export const resetJoinGroup = async (slotId: string): Promise<ResetErrorResponse> => {
  const api = getAxios()
  const response = await api.post<ResetErrorResponse>(`${SCHEDULE_URL}/reset-join-group/${slotId}`)
  return response.data
}

// Delete all today's join group slots
export interface DeleteJoinGroupsResponse {
  success: boolean
  message?: string
  error?: string
  deletedCount?: number
}

export const deleteAllTodayJoinGroups = async (): Promise<DeleteJoinGroupsResponse> => {
  const api = getAxios()
  const response = await api.delete<DeleteJoinGroupsResponse>(`${SCHEDULE_URL}/join-groups/today`)
  return response.data
}

// Trigger immediate join group for a slot
export interface JoinGroupNowResponse {
  success: boolean
  message?: string
  error?: string
}

export const joinGroupNow = async (slotId: string): Promise<JoinGroupNowResponse> => {
  const api = getAxios()
  const response = await api.post<JoinGroupNowResponse>(`${SCHEDULE_URL}/join-group-now/${slotId}`)
  return response.data
}

// Reschedule all campaigns
export interface RescheduleAllCampaignsResponse {
  success: boolean
  message?: string
  error?: string
  updatedCount?: number
  createdCount?: number
  totalCampaigns?: number
}

export const rescheduleAllCampaigns = async (): Promise<RescheduleAllCampaignsResponse> => {
  const api = getAxios()
  const response = await api.post<RescheduleAllCampaignsResponse>(`${SCHEDULE_URL}/reschedule-all-campaigns`)
  return response.data
}

// Preview reschedule all campaigns - Pirsomat style
export interface ProfileUsage {
  _id: string
  name: string
  facebook_id?: string
  posted_today: number
  max_daily: number
  max_join_groups?: number
  remaining: number
  at_limit: boolean
  status: 'at_limit' | 'high' | 'available'
  groups_joined?: number
  groups_not_joined?: number
  total_groups?: number
  posts_to_add?: number
}

export interface CampaignWarning {
  type: string
  message: string
}

export interface CampaignPreviewItem {
  _id: string
  name: string
  groupsCount: number
  profilesCount: number
  estimatedNew: number
  joinGroupsNeeded?: number
  profileUsage: ProfileUsage[]
  warnings: CampaignWarning[]
}

export interface ProfileJoinGroup {
  _id: string
  name: string
  facebook_id?: string
  max_join_groups?: number
  groups_to_join_total: number
  groups_to_join_today: number
  groups_remaining: number
}

export interface TimeSlot {
  hour: number
  count: number
}

export interface ProfileTimeSlot {
  _id: string
  name: string
  facebook_id?: string
  slots: TimeSlot[]
}

export interface ReschedulePreviewResponse {
  success: boolean
  date: string
  allow_duplicate_posts?: boolean
  willDelete: number
  willKeep: number
  estimatedCreate: number
  totalJoinGroups?: number
  totalCampaigns: number
  campaigns: CampaignPreviewItem[]
  profileJoinGroups?: ProfileJoinGroup[]
  timeSlots?: TimeSlot[]
  profileTimeSlots?: ProfileTimeSlot[]
  error?: string
}

export const getReschedulePreview = async (): Promise<ReschedulePreviewResponse> => {
  const api = getAxios()
  const response = await api.get<ReschedulePreviewResponse>(`${SCHEDULE_URL}/reschedule-all-campaigns/preview`)
  return response.data
}

// Toggle full-capacity duplicate mode for this account (admin_switch only):
// the same post may be scheduled to the same group again via DIFFERENT profiles.
export const setDuplicatePostsMode = async (enabled: boolean): Promise<{success: boolean; allow_duplicate_posts?: boolean; error?: string}> => {
  const api = getAxios()
  const response = await api.post(`${SCHEDULE_URL}/duplicate-posts-mode`, {enabled})
  return response.data
}

// Verify post existence on Facebook
export interface VerifyPostResponse {
  success: boolean
  message?: string
  error?: string
  verification_status?: 'pending' | 'verified' | 'deleted' | 'error'
  exists?: boolean
}

export const verifyPost = async (slotId: string): Promise<VerifyPostResponse> => {
  const api = getAxios()
  const response = await api.post<VerifyPostResponse>(`${SCHEDULE_URL}/verify-post/${slotId}`)
  return response.data
}

// Update max join groups per day for a profile
export interface UpdateMaxJoinGroupsResponse {
  success: boolean
  profile_id?: string
  max_join_groups_per_day?: number
  error?: string
}

export const updateMaxJoinGroups = async (profileId: string, maxJoinGroups: number): Promise<UpdateMaxJoinGroupsResponse> => {
  const api = getAxios()
  const response = await api.put<UpdateMaxJoinGroupsResponse>(`${SCHEDULE_URL}/profile/${profileId}/max-join-groups`, {
    max_join_groups: maxJoinGroups
  })
  return response.data
}

// Admin-only: per-profile override of the global 7/day join cap.
// Pass null to clear the override and go back to the normal pacing rules.
export interface UpdateJoinCapOverrideResponse {
  success: boolean
  join_cap_override?: number
  effective_join_cap?: number
  groups_following?: number
  error?: string
}

export const updateProfileJoinCapOverride = async (profileId: string, override: number | null): Promise<UpdateJoinCapOverrideResponse> => {
  const api = getAxios()
  const response = await api.put<UpdateJoinCapOverrideResponse>(`${SCHEDULE_URL}/profile/${profileId}/join-cap-override`, {
    override
  })
  return response.data
}

// ---- Per-profile group blacklist -------------------------------------------
// Groups the BOT marked unusable for a profile after cannot_post_in_group /
// group_unavailable failures. Scheduling skips these pairs, so a profile can
// quietly fall to 0 posts once its whole campaign set lands here. Listing is
// open to the account owner; removing entries is admin-only.
export interface BlacklistedGroup {
  _id: string
  name: string
  group_id?: string | null
  image_url?: string | null
  is_for_sale_group?: boolean
  is_member?: boolean
  in_campaign?: boolean
  posts_count?: number
  last_posted?: string | null
  last_error?: string | null
  last_error_at?: string | null
  // false = the message is the profile's newest failure but names a different
  // group — context, not this group's reason.
  last_error_matches_group?: boolean
}

export interface BlacklistedGroupsResponse {
  success: boolean
  can_edit?: boolean
  profile_id?: string
  groups: BlacklistedGroup[]
  error?: string
}

export const getProfileBlacklistedGroups = async (profileId: string): Promise<BlacklistedGroupsResponse> => {
  const api = getAxios()
  const response = await api.get<BlacklistedGroupsResponse>(`${SCHEDULE_URL}/profile/${profileId}/blacklisted-groups`)
  return response.data
}

// Un-block one group for one profile. Does NOT touch group membership.
export const unblacklistProfileGroup = async (profileId: string, groupId: string): Promise<{success: boolean; remaining?: number; error?: string}> => {
  const api = getAxios()
  const response = await api.delete(`${SCHEDULE_URL}/profile/${profileId}/blacklisted-groups/${groupId}`)
  return response.data
}

// Clear the whole blacklist of ONE profile.
export const clearProfileBlacklist = async (profileId: string): Promise<{success: boolean; removed?: number; error?: string}> => {
  const api = getAxios()
  const response = await api.delete(`${SCHEDULE_URL}/profile/${profileId}/blacklisted-groups`)
  return response.data
}

export interface BulkJoinCapResponse {
  success: boolean
  join_cap_override?: number
  profiles_updated?: number
  error?: string
}

// Set (or clear, with 0/null) the daily join cap on EVERY profile of the account.
export const updateAllProfilesJoinCapOverride = async (override: number | null): Promise<BulkJoinCapResponse> => {
  const api = getAxios()
  const response = await api.put<BulkJoinCapResponse>(`${SCHEDULE_URL}/profiles/join-cap-override`, {
    override
  })
  return response.data
}

export interface UpdatePostsPerDayResponse {
  success: boolean
  posts_per_day: number
}

export interface JoinCampaignGroupsResponse {
  success: boolean
  joinGroupsCreated: number
  message?: string
  error?: string
}

export const joinCampaignGroups = async (campaignId: string, maxJoinGroups?: number): Promise<JoinCampaignGroupsResponse> => {
  const api = getAxios()
  const body: any = {}
  if (maxJoinGroups !== undefined) body.max_join_groups = maxJoinGroups
  const response = await api.post<JoinCampaignGroupsResponse>(`${SCHEDULE_URL}/join-campaign-groups/${campaignId}`, body)
  return response.data
}

export const joinCampaignGroupsForProfile = async (campaignId: string, profileId: string, count: number): Promise<{success: boolean; joinGroupsCreated?: number; capped?: boolean; message?: string; error?: string}> => {
  const api = getAxios()
  const response = await api.post(`${SCHEDULE_URL}/join-campaign-groups-for-profile/${campaignId}`, {profile_id: profileId, count})
  return response.data
}

// Bulk: many profiles each join `count` new groups from EACH selected campaign.
export const joinCampaignGroupsForProfilesBulk = async (profileIds: string[], campaignIds: string[], count: number): Promise<{success: boolean; joinGroupsCreated?: number; profiles?: number; campaigns?: number; message?: string; error?: string}> => {
  const api = getAxios()
  const response = await api.post(`${SCHEDULE_URL}/join-campaign-groups-for-profiles`, {profile_ids: profileIds, campaign_ids: campaignIds, count})
  return response.data
}

// Admin (logged in as client) — create a blank profile record (no QR spawned).
export const adminAddProfile = async (): Promise<{success: boolean; profile_id?: string; error?: string}> => {
  const api = getAxios()
  const response = await api.post(`${SCHEDULE_URL}/admin/add-profile`, {})
  return response.data
}

// Admin (logged in as client) — soft-delete a profile (hidden=true).
export const adminSoftDeleteProfile = async (profileId: string): Promise<{success: boolean; error?: string}> => {
  const api = getAxios()
  const response = await api.post(`${SCHEDULE_URL}/admin/profile/${profileId}/soft-delete`, {})
  return response.data
}

export const updateProfilePostsPerDay = async (profileId: string, postsPerDay: number): Promise<UpdatePostsPerDayResponse> => {
  const api = getAxios()
  const response = await api.put<UpdatePostsPerDayResponse>(`${SCHEDULE_URL}/profile/${profileId}/posts-per-day`, {
    posts_per_day: postsPerDay
  })
  return response.data
}
